import * as RadixToast from '@radix-ui/react-toast';
import { CircleAlert, CircleCheck, Info, X, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useNotices, type Notice, type NoticeTone } from '../../lib/notify.js';

/**
 * Where a confirmation appears.
 *
 * ON RADIX, for the reason `Dialog` is: the parts of a toast that are easy to
 * get wrong are the parts nobody tests. Radix owns the pair of live regions, the
 * dismiss timer and its pause on hover and on focus, the swipe gesture, the F8
 * hotkey that moves focus into the stack, and the rule that a toast never steals
 * focus from the form that produced it. Hand-rolling that is a day of work and a
 * screen reader that says nothing.
 *
 * THE TONES ARE `Banner`'s TONES, deliberately duplicated as a small map rather
 * than by rendering a `Banner` inside a toast: the surfaces need different
 * chrome (a toast has a close button, a shadow and a solid ground so it reads
 * against whatever it covers) but they must not become different vocabularies.
 * The icon carries the status without colour — §1.3, same as the banner.
 *
 * `z-[60]` because `Dialog` is `z-50`. Most of these are triggered from inside a
 * modal, and a confirmation rendered behind the overlay that produced it is
 * worse than no confirmation: the user concludes nothing happened.
 */
const TONES: Record<NoticeTone, { styles: string; Icon: LucideIcon }> = {
  success: { styles: 'border-success/25 bg-success-surface text-success-strong', Icon: CircleCheck },
  error: { styles: 'border-danger/25 bg-danger-surface text-danger', Icon: CircleAlert },
  info: { styles: 'border-line bg-white text-primary-700', Icon: Info },
};

/**
 * A failure stays twice as long as a confirmation.
 *
 * "Channel disimpan." is read in a glance and the screen behind it already shows
 * the new row. A failure is a sentence naming a cause, and it is the only copy
 * of that sentence — a row-button failure has no banner to fall back on.
 */
const DURATION: Record<NoticeTone, number> = {
  success: 4000,
  info: 5000,
  error: 9000,
};

/** Must match `.toast[data-state='closed']` in `index.css`. */
const EXIT_MS = 120;

export function Toaster() {
  const notices = useNotices((state) => state.notices);
  const dismiss = useNotices((state) => state.dismiss);

  return (
    <RadixToast.Provider swipeDirection="right">
      {notices.map((notice) => (
        <Toast key={notice.id} notice={notice} onDismissed={() => dismiss(notice.id)} />
      ))}

      {/*
        Bottom-right on a pointer viewport, where it is out of the way of a
        dialog's footer buttons. Full width at the bottom on the mobile
        fallback, because a 24rem card floated over a 375px screen is the
        screen.

        `pointer-events-none` on the list with `pointer-events-auto` on each
        toast: an empty viewport is an invisible fixed-position box, and one
        sitting over the corner of a table would swallow clicks on the rows
        underneath it.
      */}
      <RadixToast.Viewport className="pointer-events-none fixed bottom-0 right-0 z-[60] m-0 flex w-full max-w-full list-none flex-col gap-2 p-4 outline-none sm:max-w-sm [&>li]:pointer-events-auto" />
    </RadixToast.Provider>
  );
}

/**
 * One toast, owning its own `open` so it can animate out.
 *
 * THE STORE IS NOT THE SOURCE OF `open`, and that is the whole reason this is a
 * component rather than a `<Toast.Root>` in the map above. Dropping the notice
 * from the store the moment it closes unmounts the element on the same frame,
 * so the exit animation never runs — the toast vanishes rather than leaving.
 * Closing locally and clearing the store a beat later gives Radix something to
 * animate, and keeps the store from growing for the rest of the session.
 */
function Toast({ notice, onDismissed }: { notice: Notice; onDismissed: () => void }) {
  const [open, setOpen] = useState(true);
  const { styles, Icon } = TONES[notice.tone];

  /*
   * A timer rather than `animationend`: the reduced-motion rule in `index.css`
   * collapses the animation to 0.001ms, and a listener that a browser decides
   * not to fire would strand the notice in the store forever. Cleared on unmount
   * so a `Toaster` that goes away mid-exit does not set state on a dead
   * component.
   */
  const exit = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(exit.current), []);

  return (
    <RadixToast.Root
      open={open}
      /*
       * `foreground` interrupts, `background` waits its turn. A failure is the
       * only thing on screen saying the action did not happen, so it is
       * announced now; a confirmation follows whatever the user is already
       * hearing.
       */
      type={notice.tone === 'error' ? 'foreground' : 'background'}
      duration={DURATION[notice.tone]}
      onOpenChange={(next) => {
        if (next) return;
        setOpen(false);
        exit.current = setTimeout(onDismissed, EXIT_MS);
      }}
      className={`toast flex items-start gap-2.5 rounded-xl border p-3 pr-2 text-sm shadow-card ${styles}`}
    >
      <Icon size={18} className="mt-px shrink-0" aria-hidden />
      <RadixToast.Description className="min-w-0 flex-1 py-px">{notice.message}</RadixToast.Description>
      <RadixToast.Close
        aria-label="Tutup"
        className="focus-ring -m-1 shrink-0 rounded-lg p-1 opacity-60 transition-opacity hover:opacity-100"
      >
        <X size={16} aria-hidden />
      </RadixToast.Close>
    </RadixToast.Root>
  );
}
